'use client';

import { useRouter } from 'next/navigation';
import { BookOpen, Building2, Loader2, Search, Users, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import {
  normalizePeopleSearchQuery,
  readPeopleDiscoveryResponse,
  type PeopleDiscoveryPerson,
} from '@/lib/people-discovery';
import {
  STORY_SEARCH_QUERY_MIN,
  readStorySearchResponse,
  type StorySearchResult,
} from '@/lib/story-search';
import { trackEvent } from '@/lib/analyticsEvents';
import { useTranslation } from '@/i18n/useTranslation';
import type { TranslationKey } from '@/i18n/useTranslation';

export type SearchScope = 'all' | 'people' | 'stories';

const SCOPE_LABEL_KEYS: Record<SearchScope, TranslationKey> = {
  all: 'scoped_search.scope_all' as TranslationKey,
  people: 'scoped_search.scope_people' as TranslationKey,
  stories: 'scoped_search.scope_stories' as TranslationKey,
};

type ScopeStatus = 'idle' | 'loading' | 'success' | 'error';

function describeFailure(
  response: Response | null,
  scope: 'people' | 'stories',
  t: (key: TranslationKey, vars?: Record<string, string>) => string
): string {
  if (!response) return t('scoped_search.network_error' as TranslationKey);
  if (response.status === 401) return t('scoped_search.sign_in_again' as TranslationKey);
  if (response.status === 429) return t('scoped_search.rate_limited' as TranslationKey);
  return scope === 'people'
    ? t('scoped_search.generic_error' as TranslationKey)
    : t('scoped_search.stories_unavailable' as TranslationKey);
}

type CombinedRow =
  | { kind: 'person'; person: PeopleDiscoveryPerson }
  | { kind: 'story'; story: StorySearchResult };

export type ScopedSearchDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialScope?: SearchScope;
  returnFocusRef?: RefObject<HTMLElement | null>;
  onPersonSelect?: (person: PeopleDiscoveryPerson) => void;
  onStorySelect?: (story: StorySearchResult) => void;
};

function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/**
 * Global search across the scopes that have real backing today: people
 * (/api/network/search) and public stories (/api/search/stories). Additional
 * scopes join as their APIs land — the dialog only ever offers scopes it can
 * actually search. A11y model (focus trap, roving active descendant, abortable
 * requests, focus restore) follows PeopleSearchDialog.
 */
export default function ScopedSearchDialog({
  open,
  onOpenChange,
  initialScope = 'all',
  returnFocusRef,
  onPersonSelect,
  onStorySelect,
}: ScopedSearchDialogProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const resultsId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const capturedFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const requestSeqRef = useRef(0);
  const controllersRef = useRef<AbortController[]>([]);

  const [scope, setScope] = useState<SearchScope>(initialScope);
  const [query, setQuery] = useState('');
  const [people, setPeople] = useState<PeopleDiscoveryPerson[]>([]);
  const [stories, setStories] = useState<StorySearchResult[]>([]);
  const [peopleStatus, setPeopleStatus] = useState<ScopeStatus>('idle');
  const [storyStatus, setStoryStatus] = useState<ScopeStatus>('idle');
  const [peopleError, setPeopleError] = useState(() => t('scoped_search.generic_error' as any));
  const [storyError, setStoryError] = useState(() => t('scoped_search.generic_error' as any));
  const [activeIndex, setActiveIndex] = useState(-1);
  const [retryNonce, setRetryNonce] = useState(0);

  const normalizedQuery = useMemo(() => normalizePeopleSearchQuery(query), [query]);
  const wantsPeople = scope === 'all' || scope === 'people';
  const wantsStories = scope === 'all' || scope === 'stories';

  const rows: CombinedRow[] = useMemo(() => {
    const combined: CombinedRow[] = [];
    if (wantsPeople) for (const person of people) combined.push({ kind: 'person', person });
    if (wantsStories) for (const story of stories) combined.push({ kind: 'story', story });
    return combined;
  }, [people, stories, wantsPeople, wantsStories]);

  const closeDialog = useCallback(() => onOpenChange(false), [onOpenChange]);

  const abortAll = useCallback(() => {
    for (const controller of controllersRef.current) controller.abort();
    controllersRef.current = [];
  }, []);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      capturedFocusRef.current = returnFocusRef?.current ?? (document.activeElement as HTMLElement);
      setQuery('');
      setScope(initialScope);
      setPeople([]);
      setStories([]);
      setPeopleStatus('idle');
      setStoryStatus('idle');
      setActiveIndex(-1);
      wasOpenRef.current = true;
      trackEvent('people_search_opened');
    } else if (!open && wasOpenRef.current) {
      abortAll();
      const focusTarget = returnFocusRef?.current ?? capturedFocusRef.current;
      if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
      capturedFocusRef.current = null;
      wasOpenRef.current = false;
    }
  }, [abortAll, initialScope, open, returnFocusRef]);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document
      .getElementById(`${resultsId}-option-${activeIndex}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open, resultsId]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (normalizedQuery.length < STORY_SEARCH_QUERY_MIN || normalizedQuery.length > 64) {
      abortAll();
      setPeople([]);
      setStories([]);
      setPeopleStatus('idle');
      setStoryStatus('idle');
      setActiveIndex(-1);
      return;
    }

    const seq = ++requestSeqRef.current;
    abortAll();
    if (wantsPeople) setPeopleStatus('loading');
    if (wantsStories) setStoryStatus('loading');
    setActiveIndex(-1);

    const timer = window.setTimeout(() => {
      const tasks: Promise<void>[] = [];

      if (wantsPeople) {
        const controller = new AbortController();
        controllersRef.current.push(controller);
        tasks.push(
          (async () => {
            let response: Response | null = null;
            try {
              response = await fetch(
                `/api/network/search?q=${encodeURIComponent(normalizedQuery)}&limit=8&context=global`,
                { signal: controller.signal, cache: 'no-store' }
              );
              if (!response.ok) throw new Error('people_search_failed');
              const results = readPeopleDiscoveryResponse((await response.json()) as unknown);
              if (requestSeqRef.current !== seq) return;
              setPeople(results);
              setPeopleStatus('success');
            } catch (error) {
              if (error instanceof DOMException && error.name === 'AbortError') return;
              if (requestSeqRef.current !== seq) return;
              setPeopleError(describeFailure(response, 'people', t));
              setPeople([]);
              setPeopleStatus('error');
            }
          })()
        );
      }

      if (wantsStories) {
        const controller = new AbortController();
        controllersRef.current.push(controller);
        tasks.push(
          (async () => {
            let response: Response | null = null;
            try {
              response = await fetch(
                `/api/search/stories?q=${encodeURIComponent(normalizedQuery)}&limit=6`,
                { signal: controller.signal, cache: 'no-store' }
              );
              if (!response.ok) throw new Error('story_search_failed');
              const results = readStorySearchResponse((await response.json()) as unknown);
              if (requestSeqRef.current !== seq) return;
              setStories(results);
              setStoryStatus('success');
            } catch (error) {
              if (error instanceof DOMException && error.name === 'AbortError') return;
              if (requestSeqRef.current !== seq) return;
              setStoryError(describeFailure(response, 'stories', t));
              setStories([]);
              setStoryStatus('error');
            }
          })()
        );
      }

      void Promise.all(tasks).then(() => {
        if (requestSeqRef.current === seq) setActiveIndex((index) => (index === -1 ? 0 : index));
      });
    }, 280);

    return () => window.clearTimeout(timer);
  }, [abortAll, normalizedQuery, open, retryNonce, scope, t, wantsPeople, wantsStories]);

  const chooseRow = useCallback(
    (row: CombinedRow) => {
      if (row.kind === 'person') {
        trackEvent('people_search_result_opened');
        onPersonSelect?.(row.person);
        closeDialog();
        router.push(row.person.profile_path);
        return;
      }
      onStorySelect?.(row.story);
      closeDialog();
      if (row.story.author.publicHandle) router.push(`/u/${row.story.author.publicHandle}`);
    },
    [closeDialog, onPersonSelect, onStorySelect, router]
  );

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && rows.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % rows.length);
    } else if (event.key === 'ArrowUp' && rows.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? rows.length - 1 : index - 1));
    } else if (event.key === 'Enter' && activeIndex >= 0 && rows[activeIndex]) {
      event.preventDefault();
      chooseRow(rows[activeIndex]);
    }
  };

  const handleDialogKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog();
      return;
    }
    if (event.key !== 'Tab' || !dialogRef.current) return;
    const focusable = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden'));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  const anyLoading =
    (wantsPeople && peopleStatus === 'loading') || (wantsStories && storyStatus === 'loading');
  const allIdle =
    (!wantsPeople || peopleStatus === 'idle') && (!wantsStories || storyStatus === 'idle');
  const allErrored =
    (!wantsPeople || peopleStatus === 'error') &&
    (!wantsStories || storyStatus === 'error') &&
    !allIdle;
  const emptySuccess =
    !anyLoading &&
    !allIdle &&
    rows.length === 0 &&
    ((wantsPeople && peopleStatus === 'success') || (wantsStories && storyStatus === 'success'));

  const liveMessage = anyLoading
    ? t('scoped_search.live_searching' as any)
    : allErrored
      ? t('scoped_search.live_could_not_complete' as any)
      : rows.length > 0
        ? t('scoped_search.live_results_found' as any, { count: String(rows.length) })
        : allIdle
          ? t('scoped_search.live_enter_two_chars' as any)
          : t('scoped_search.live_no_results' as any);

  let flatIndex = -1;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-stretch justify-center bg-slate-950/65 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeDialog();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onKeyDown={handleDialogKeyDown}
        data-clarity-mask="true"
        className="grid h-[100dvh] w-full grid-rows-[auto_auto_minmax(0,1fr)_auto] overflow-hidden border-dashboard bg-dashboard-surface text-dashboard-primary shadow-2xl sm:h-auto sm:max-h-[min(720px,calc(100dvh-2rem))] sm:max-w-2xl sm:rounded-2xl sm:border"
      >
        <header className="border-b border-dashboard pt-[env(safe-area-inset-top)] sm:pt-0">
          <div className="flex items-start justify-between gap-4 p-4 sm:p-5">
            <div>
              <h2 id={titleId} className="text-lg font-bold text-dashboard-primary">
                {t('scoped_search.title' as any)}
              </h2>
              <p id={descriptionId} className="mt-1 text-sm text-dashboard-secondary">
                {t('scoped_search.description' as any)}
              </p>
            </div>
            <button
              type="button"
              onClick={closeDialog}
              aria-label={t('scoped_search.close' as any)}
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-dashboard text-dashboard-secondary hover:bg-dashboard-subtle hover:text-dashboard-primary"
            >
              <X size={19} aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="border-b border-dashboard p-4 sm:px-5">
          <label htmlFor={`${resultsId}-input`} className="sr-only">
            {t('scoped_search.input_label' as any)}
          </label>
          <div className="relative">
            <Search
              size={18}
              aria-hidden="true"
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-dashboard-muted"
            />
            <input
              ref={inputRef}
              id={`${resultsId}-input`}
              type="search"
              role="searchbox"
              aria-controls={resultsId}
              aria-activedescendant={
                activeIndex >= 0 ? `${resultsId}-option-${activeIndex}` : undefined
              }
              autoComplete="off"
              spellCheck={false}
              maxLength={65}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={t('scoped_search.input_label' as any)}
              className="min-h-12 w-full rounded-xl border border-dashboard bg-dashboard-subtle py-3 pl-11 pr-4 text-sm text-dashboard-primary outline-none placeholder:text-dashboard-muted focus:border-dashboard-accent focus:ring-2 focus:ring-dashboard-accent/20"
            />
          </div>
          <div role="radiogroup" aria-label={t('scoped_search.scope_group_label' as any)} className="mt-3 flex items-center gap-2">
            {(Object.keys(SCOPE_LABEL_KEYS) as SearchScope[]).map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={scope === option}
                onClick={() => setScope(option)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${
                  scope === option
                    ? 'border-dashboard-accent bg-dashboard-accent/10 text-dashboard-accent'
                    : 'border-dashboard bg-dashboard-surface text-dashboard-secondary hover:bg-dashboard-subtle'
                }`}
              >
                {t(SCOPE_LABEL_KEYS[option])}
              </button>
            ))}
          </div>
          <p className="sr-only" aria-live="polite" aria-atomic="true">
            {liveMessage}
          </p>
        </div>

        <div
          id={resultsId}
          role="list"
          className="min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-4"
        >
          {anyLoading ? (
            <div className="flex min-h-52 flex-col items-center justify-center gap-3 text-dashboard-secondary">
              <Loader2
                size={24}
                className="animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              <p className="text-sm">{t('scoped_search.searching' as any)}</p>
            </div>
          ) : allIdle ? (
            <div className="flex min-h-52 flex-col items-center justify-center rounded-xl border border-dashed border-dashboard-strong bg-dashboard-subtle/80 p-6 text-center">
              <Search size={25} className="text-dashboard-muted" aria-hidden="true" />
              <p className="mt-3 font-semibold text-dashboard-primary">{t('scoped_search.title' as any)}</p>
              <p className="mt-1 max-w-sm text-sm text-dashboard-secondary">
                {t('scoped_search.idle_hint' as any)}
              </p>
            </div>
          ) : allErrored ? (
            <div className="flex min-h-52 flex-col items-center justify-center rounded-xl border border-dashed border-dashboard-strong bg-dashboard-subtle/80 p-6 text-center">
              <p className="font-semibold text-dashboard-primary">
                {t('scoped_search.unavailable' as any)}
              </p>
              <p className="mt-1 max-w-sm text-sm text-dashboard-secondary">
                {wantsPeople ? peopleError : storyError}
              </p>
              <button
                type="button"
                onClick={() => setRetryNonce((value) => value + 1)}
                className="mt-4 min-h-11 rounded-xl bg-dashboard-accent px-4 text-sm font-bold text-white"
              >
                {t('scoped_search.try_again' as any)}
              </button>
            </div>
          ) : emptySuccess ? (
            <div className="flex min-h-52 flex-col items-center justify-center rounded-xl border border-dashed border-dashboard-strong bg-dashboard-subtle/80 p-6 text-center">
              <Users size={25} className="text-dashboard-muted" aria-hidden="true" />
              <p className="mt-3 font-semibold text-dashboard-primary">{t('scoped_search.no_results' as any)}</p>
              <p className="mt-1 max-w-sm text-sm text-dashboard-secondary">
                {t('scoped_search.no_results_hint' as any)}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {wantsPeople && (people.length > 0 || peopleStatus === 'error') ? (
                <section aria-label={t('scoped_search.scope_people' as any)}>
                  <h3 className="px-1 pb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-dashboard-muted">
                    {t('scoped_search.scope_people' as any)}
                  </h3>
                  {peopleStatus === 'error' ? (
                    <p className="px-1 text-sm text-dashboard-secondary">{peopleError}</p>
                  ) : (
                    <div className="space-y-1.5">
                      {people.map((person) => {
                        flatIndex += 1;
                        const index = flatIndex;
                        return (
                          <button
                            key={person.public_handle}
                            id={`${resultsId}-option-${index}`}
                            type="button"
                            role="listitem"
                            onClick={() => chooseRow({ kind: 'person', person })}
                            onMouseEnter={() => setActiveIndex(index)}
                            className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left ${
                              activeIndex === index
                                ? 'border-dashboard-accent bg-dashboard-subtle'
                                : 'border-transparent bg-dashboard-surface'
                            }`}
                          >
                            <span
                              className={`flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden bg-dashboard-elevated text-xs font-bold text-dashboard-secondary ${
                                person.role === 'institution' ? 'rounded-xl' : 'rounded-full'
                              }`}
                            >
                              {person.avatar_url ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                  src={person.avatar_url}
                                  alt=""
                                  className="h-full w-full object-cover"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                initialsFor(person.display_name)
                              )}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex min-w-0 items-center gap-1.5">
                                <span className="truncate text-sm font-bold text-dashboard-primary">
                                  {person.display_name}
                                </span>
                                {person.role === 'institution' ? (
                                  <Building2
                                    size={14}
                                    aria-label={t('scoped_search.institution_label' as any)}
                                    className="flex-shrink-0 text-dashboard-accent"
                                  />
                                ) : null}
                              </span>
                              <span className="block truncate text-xs font-medium text-dashboard-secondary">
                                @{person.public_handle}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </section>
              ) : null}

              {wantsStories && (stories.length > 0 || storyStatus === 'error') ? (
                <section aria-label={t('scoped_search.scope_stories' as any)}>
                  <h3 className="px-1 pb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-dashboard-muted">
                    {t('scoped_search.scope_stories' as any)}
                  </h3>
                  {storyStatus === 'error' ? (
                    <p className="px-1 text-sm text-dashboard-secondary">{storyError}</p>
                  ) : (
                    <div className="space-y-1.5">
                      {stories.map((story) => {
                        flatIndex += 1;
                        const index = flatIndex;
                        return (
                          <button
                            key={story.id}
                            id={`${resultsId}-option-${index}`}
                            type="button"
                            role="listitem"
                            onClick={() => chooseRow({ kind: 'story', story })}
                            onMouseEnter={() => setActiveIndex(index)}
                            className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left ${
                              activeIndex === index
                                ? 'border-dashboard-accent bg-dashboard-subtle'
                                : 'border-transparent bg-dashboard-surface'
                            }`}
                          >
                            <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-dashboard-elevated text-dashboard-secondary">
                              <BookOpen size={14} aria-hidden="true" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-bold text-dashboard-primary">
                                {story.title}
                              </span>
                              {story.snippet ? (
                                <span className="mt-0.5 block truncate text-xs text-dashboard-secondary">
                                  {story.snippet}
                                </span>
                              ) : null}
                              <span className="mt-0.5 block truncate text-[11px] text-dashboard-muted">
                                {story.author.fullName}
                                {story.author.publicHandle
                                  ? ` · @${story.author.publicHandle}`
                                  : ''}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </section>
              ) : null}
            </div>
          )}
        </div>

        <footer className="border-t border-dashboard px-4 pb-[env(safe-area-inset-bottom)] text-xs text-dashboard-muted sm:px-5">
          <div className="flex min-h-12 items-center justify-between gap-3 py-2">
            <p>{t('scoped_search.footer_note' as any)}</p>
            <span className="hidden flex-shrink-0 sm:inline" aria-hidden="true">
              {t('scoped_search.footer_shortcuts' as any)}
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}
